import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useTemplate } from '../../../../../queries/Templates/useTemplate';
import { useUserPreferencesMutation } from '../../../../../queries/User/useUserPreferencesMutation';
import { TextArea } from '../../../../components-v2/TextArea';
import { Button } from '../../../Button';
import { Checkbox } from '../../../Checkbox';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS } from '../../models';
import { socialMediaSignatureStyleSheet } from './socialMediaSignatureStyles';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

function escapeHtml(input: string): string {
	return input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function extractLevContent(document: string | undefined): string {
	if (!document) {
		return '';
	}
	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(document, 'text/html');
		const levContentElement = doc.querySelector('lev-content');
		// textContent auto-decodes HTML entities written via escapeHtml on save. No wrapper => empty string.
		return levContentElement?.textContent ?? '';
	} catch {
		return '';
	}
}

export const SocialMediaSignatureComposerBase: React.FC<IProps> = observer(props => {
	const { className, styles = [] } = props;
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const [signature, setSignature] = React.useState<string>('');
	const [includeDisclaimer, setIncludeDisclaimer] = React.useState<boolean>(false);
	const [isSaving, setIsSaving] = React.useState<boolean>(false);
	const [validationError, setValidationError] = React.useState<string>('');
	const [hasChanges, setHasChanges] = React.useState<boolean>(false);
	const hasAppliedAccountDefault = React.useRef(false);

	const accountTemplateId = userSession?.account?.preferences?.socialMediaSignatureTemplateId;
	const { data: accountTemplate, isPending: isLoadingAccountTemplate } = useTemplate({
		templateId: accountTemplateId,
		enabled: !!accountTemplateId,
		refetchOnMount: 'always',
		impersonationContext: userSession?.impersonationContext,
	});

	const currentTemplateId = userSession?.user?.userPreferences?.socialMediaSignatureTemplateId;
	const { data: existingTemplate, isPending: isLoadingTemplate } = useTemplate({
		templateId: currentTemplateId,
		enabled: !!currentTemplateId && !accountTemplateId,
		refetchOnMount: 'always',
		impersonationContext: userSession?.impersonationContext,
	});
	const { mutate: updateUserPreferences } = useUserPreferencesMutation({
		impersonationContext: userSession?.impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error, 'Failed to update user preferences');
		},
		onSuccess: data => {
			// Sync the server response into the in-memory userSession — otherwise `currentTemplateId`
			// stays stale and a subsequent Reset can't tell there's a saved template to clear.
			if (userSession?.user) {
				userSession.updateUser({
					...userSession.user,
					userPreferences: data,
				});
			}
		},
	});
	const templatesVm = React.useMemo(() => {
		return new Api.TemplatesViewModel(userSession).impersonate(userSession?.impersonationContext);
	}, [userSession]);

	const accountDefaultText = React.useMemo(
		() => extractLevContent(accountTemplate?.content?.document),
		[accountTemplate?.content?.document]
	);

	const saveSignature = React.useCallback(async () => {
		if (!includeDisclaimer) {
			updateUserPreferences({
				preferences: {
					...userSession?.user?.userPreferences,
					socialMediaSignatureTemplateId: '',
				},
			});
			setHasChanges(false);
			toast.success('Social media signature cleared');
			return;
		}

		if (!signature.trim()) {
			return;
		}

		// Saving with no edits to the account default — don't create a redundant user template.
		// Leave the user relying on the account fallback (clear the templateId reference if one lingers).
		if (!!accountDefaultText && signature === accountDefaultText) {
			if (currentTemplateId) {
				updateUserPreferences({
					preferences: {
						...userSession?.user?.userPreferences,
						socialMediaSignatureTemplateId: '',
					},
				});
			}
			setHasChanges(false);
			toast.success('Social media signature saved successfully');
			return;
		}

		setIsSaving(true);
		try {
			const wrappedContent = `<lev-content>${escapeHtml(signature)}</lev-content>`;
			if (currentTemplateId && existingTemplate) {
				const updatedTemplate: Api.ITemplate = {
					...existingTemplate,
					id: currentTemplateId,
					content: {
						document: wrappedContent,
					},
				};

				await templatesVm.update(updatedTemplate);
			} else {
				const template: Api.ITemplate = {
					name: 'Social Media Signature',
					templateType: Api.TemplateType.SocialMediaSignature,
					content: {
						document: wrappedContent,
					},
					scope: Api.TemplateScope.User,
					defaultTemplate: true,
				};

				const createdTemplate = await templatesVm.create(template);

				if (!createdTemplate?.id) {
					throw new Error('Template create did not return an id');
				}
				updateUserPreferences({
					preferences: {
						...userSession?.user?.userPreferences,
						socialMediaSignatureTemplateId: createdTemplate.id,
					},
				});
			}

			setHasChanges(false);
			toast.success('Social media signature saved successfully');
		} catch (error) {
			errorMessages.pushApiError(error, 'Failed to save social media signature');
		} finally {
			setIsSaving(false);
		}
	}, [
		includeDisclaimer,
		signature,
		templatesVm,
		errorMessages,
		updateUserPreferences,
		userSession,
		currentTemplateId,
		existingTemplate,
		accountDefaultText,
	]);

	// Reset the latch when the account or impersonation context changes — otherwise the user would never
	// see a different account's default after switching impersonation, or an updated default after the
	// admin saves a new one.
	React.useEffect(() => {
		hasAppliedAccountDefault.current = false;
	}, [accountTemplateId, userSession?.impersonationContext]);

	React.useEffect(() => {
		if (isLoadingTemplate && !!currentTemplateId) {
			return;
		}

		// Existing user template wins over an unacknowledged account default — never clobber the user's content.
		if (currentTemplateId && existingTemplate?.content?.document) {
			const content = extractLevContent(existingTemplate.content.document);
			if (content) {
				setSignature(content);
				setIncludeDisclaimer(true);
				setHasChanges(false);
			}
			return;
		}

		// No user template — pre-populate from the account default if one is set.
		if (!!accountTemplateId && !hasAppliedAccountDefault.current && !isLoadingAccountTemplate) {
			// Only latch the ref once we've actually applied real content — otherwise a transient empty
			// fetch would lock out future updates.
			if (accountDefaultText) {
				hasAppliedAccountDefault.current = true;
				setSignature(accountDefaultText);
				setIncludeDisclaimer(true);
				setHasChanges(false);
			}
			return;
		}

		// Nothing anywhere — blank the form. Guarded on !accountTemplateId so we don't clobber state
		// after a save-while-unchecked (which leaves currentTemplateId empty but the account default valid).
		if (!currentTemplateId && !accountTemplateId) {
			setSignature('');
			setIncludeDisclaimer(false);
			setHasChanges(false);
		}
	}, [
		currentTemplateId,
		isLoadingTemplate,
		existingTemplate?.content?.document,
		accountDefaultText,
		accountTemplateId,
		isLoadingAccountTemplate,
	]);

	// Derived: the badge reflects "currently relying on the admin's default verbatim", which is fully
	// determined by these four values. Keeping it as state caused stale/missed transitions.
	const isFromAccountDefault =
		!currentTemplateId && includeDisclaimer && !!accountDefaultText && signature === accountDefaultText;
	const canResetToAccountDefault = !!accountDefaultText && includeDisclaimer && signature !== accountDefaultText;

	const handleResetToAccountDefault = React.useCallback(() => {
		if (!accountDefaultText) {
			return;
		}

		// Delete the user's saved disclaimer reference so they rely on the account-level fallback.
		// Always fire the mutation — userSession can lag behind the server (e.g., right after a Save
		// that just created a template), so we can't trust `currentTemplateId` alone to decide.
		updateUserPreferences({
			preferences: {
				...userSession?.user?.userPreferences,
				socialMediaSignatureTemplateId: '',
			},
		});

		// Reset the latch so the load effect re-applies the account default once the cleared
		// preference flows back through user session.
		hasAppliedAccountDefault.current = false;
		setSignature(accountDefaultText);
		setIncludeDisclaimer(true);
		setHasChanges(false);
		setValidationError('');
		toast.success('Reset to account default');
	}, [accountDefaultText, updateUserPreferences, userSession]);

	const handleSignatureChange = React.useCallback((value: string) => {
		setSignature(value);
		setHasChanges(true);

		if (value.length > SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS) {
			setValidationError(`Signature must be ${SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS} characters or less`);
		} else {
			setValidationError('');
		}
	}, []);

	const handleCheckboxChange = React.useCallback(
		(checked: boolean) => {
			setIncludeDisclaimer(checked);
			setHasChanges(true);

			// Re-enabling with no saved user template — restore the account default text so the user
			// sees what will be used and the derived badge re-appears. We unconditionally restore here
			// (when there's no user template) because the only way to lose the disclaimer state and
			// come back to it is to deliberately uncheck — preserving stale typing isn't valuable.
			if (checked && !currentTemplateId && !!accountDefaultText) {
				setSignature(accountDefaultText);
			}
		},
		[currentTemplateId, accountDefaultText]
	);
	const handleCancel = React.useCallback(() => {
		const userTemplateText = extractLevContent(existingTemplate?.content?.document);
		if (userTemplateText) {
			setSignature(userTemplateText);
			setIncludeDisclaimer(true);
			setHasChanges(false);
			return;
		}
		// No user template — revert to the account default if one is available so Cancel restores the
		// baseline the user saw on mount, not a blank form.
		if (accountDefaultText) {
			setSignature(accountDefaultText);
			setIncludeDisclaimer(true);
			setHasChanges(false);
			return;
		}
		setSignature('');
		setIncludeDisclaimer(false);
		setHasChanges(false);
	}, [existingTemplate, accountDefaultText]);

	const handleSave = React.useCallback(() => {
		saveSignature();
	}, [saveSignature]);
	if (accountTemplateId) {
		if (isLoadingAccountTemplate) {
			return (
				<div className={css(socialMediaSignatureStyleSheet.loadingContainer)}>
					<LoadingSpinner type='small' />
					<span>Loading signature...</span>
				</div>
			);
		}
		return (
			<div className={`${css(socialMediaSignatureStyleSheet.container, ...styles)} ${className || ''}`}>
				<div className={css(socialMediaSignatureStyleSheet.lockedNotice)}>
					This disclaimer was set by your account admin and is applied to all of your social media posts. Contact your
					admin to change it.
				</div>
				<TextArea
					readOnly
					rows={4}
					styles={[socialMediaSignatureStyleSheet.lockedTextArea]}
					value={accountDefaultText}
				/>
			</div>
		);
	}

	if (isLoadingTemplate && !!currentTemplateId) {
		return (
			<div className={css(socialMediaSignatureStyleSheet.loadingContainer)}>
				<LoadingSpinner type='small' />
				<span>Loading signature...</span>
			</div>
		);
	}

	return (
		<div className={`${css(socialMediaSignatureStyleSheet.container, ...styles)} ${className || ''}`}>
			<div className={css(socialMediaSignatureStyleSheet.checkboxContainer)}>
				<Checkbox
					checked={includeDisclaimer}
					className={css(socialMediaSignatureStyleSheet.checkbox)}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e.target.checked)}
				>
					<span>Include disclaimer language at the bottom of each social media post</span>
				</Checkbox>
			</div>

			{includeDisclaimer && (isFromAccountDefault || canResetToAccountDefault) && (
				<div className={css(socialMediaSignatureStyleSheet.accountDefaultRow)}>
					{isFromAccountDefault && (
						<div className={css(socialMediaSignatureStyleSheet.accountDefaultBadge)}>
							Set by your account admin — you can edit or clear this
						</div>
					)}
					{canResetToAccountDefault && (
						<Button
							className={css(socialMediaSignatureStyleSheet.resetButton)}
							kind='link'
							onClick={handleResetToAccountDefault}
						>
							Reset to account default
						</Button>
					)}
				</div>
			)}

			{includeDisclaimer && (
				<>
					<div className={css(socialMediaSignatureStyleSheet.fieldGroup)}>
						<label className={css(socialMediaSignatureStyleSheet.label)}>Disclaimer Text</label>
						<TextArea
							maxLength={SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleSignatureChange(e.target.value)}
							placeholder='Enter your disclaimer text...'
							rows={4}
							styles={[socialMediaSignatureStyleSheet.textArea]}
							value={signature}
						/>
						{validationError && (
							<div className={css(socialMediaSignatureStyleSheet.errorMessage)}>{validationError}</div>
						)}
					</div>

					<div className={css(socialMediaSignatureStyleSheet.characterCountContainer)}>
						<div
							className={css(
								socialMediaSignatureStyleSheet.characterCount,
								signature.length > SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS &&
									socialMediaSignatureStyleSheet.characterCountError
							)}
						>
							{signature.length} / {SOCIAL_MEDIA_DISCLAIMER_MAX_CHARS} characters
						</div>
					</div>
				</>
			)}

			{hasChanges && (
				<div className={css(socialMediaSignatureStyleSheet.buttonGroup)}>
					<Button
						className={css(socialMediaSignatureStyleSheet.saveButton)}
						disabled={(includeDisclaimer && (!signature.trim() || !!validationError)) || isSaving || !hasChanges}
						isLoading={isSaving}
						onClick={handleSave}
						kind='primary'
					>
						{isSaving ? (
							<>
								<LoadingSpinner type='tiny' />
								Saving...
							</>
						) : (
							'Save'
						)}
					</Button>
					<Button
						className={css(socialMediaSignatureStyleSheet.cancelButton)}
						disabled={isSaving}
						onClick={handleCancel}
						kind='reverse'
					>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
});

export const SocialMediaSignatureComposer = inject(ImpersonationContextKey)(SocialMediaSignatureComposerBase);
